"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
export default function Faq() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    numeral: "01.",
    question: "How does Evermark ensure material integrity?",
    answer: "Quality is the foundation of our manufacturing process. Every phenolic panel undergoes rigorous stress testing and thermal analysis to meet international durability standards. We utilize high-pressure lamination techniques that guarantee resistance to moisture, impact, and chemical degradation in demanding environments."
  }, {
    numeral: "02.",
    question: "What are the lead times for custom panel orders?",
    answer: "Standard production cycles typically span four to six weeks from the finalization of technical specifications. For bespoke architectural projects requiring custom fabrication or specialized finishes, we recommend an eight to twelve-week lead time to ensure precision and quality control throughout the manufacturing phase."
  }, {
    numeral: "03.",
    question: "Does Evermark provide international shipping?",
    answer: "Yes. We maintain a robust logistics network capable of delivering our high-performance panels to project sites globally. Our team coordinates with international freight partners to ensure secure crating and timely delivery, adhering to all regional import regulations and site-specific requirements."
  }, {
    numeral: "04.",
    question: "How are project pricing structures determined?",
    answer: "Pricing is tailored to the scope and technical requirements of your specific application. We provide transparent, itemized quotes based on material volume, custom fabrication needs, and delivery logistics. A detailed estimate is prepared following a comprehensive review of your architectural drawings."
  }, {
    numeral: "05.",
    question: "How does Evermark handle technical consultations?",
    answer: "Prior to project commencement, our engineering team conducts a thorough review of your site conditions and performance requirements. This ensures that our material specifications are perfectly aligned with your structural needs. This technical assessment is completed within a few business days of your initial inquiry."
  }, {
    numeral: "06.",
    question: "What is the difference between standard and fire-rated panels?",
    answer: "Standard panels are engineered for high-traffic interior applications requiring durability and aesthetic finish. Fire-rated panels incorporate specialized additives that significantly enhance flame spread resistance and smoke development ratings, meeting stringent building codes for public and commercial infrastructure."
  }];
  return <section data-section-id="3573" id="faq" className="bg-background py-32 font-default">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div ref={headerRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="text-xs uppercase tracking-widest text-primary font-sans mb-8">
                Frequently Asked
              </p>
              <h2 className="font-default text-foreground text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                <span className="block">Material</span>
                <span className="block">Questions,</span>
                <span className="block italic font-light text-primary">
                  Answered.
                </span>
              </h2>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div ref={listRef} className={`transition-all duration-700 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-b border-border last:border-b-0 group">
                    <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden">
                      <div className="flex items-start gap-6 flex-1 text-left">
                        <span className="text-sm font-sans text-primary tracking-wider pt-2 shrink-0">
                          {faq.numeral}
                        </span>
                        <span className="font-default text-foreground text-2xl leading-snug tracking-tight">
                          {faq.question}
                        </span>
                      </div>
                      <span aria-hidden="true" className="font-default text-3xl font-light text-foreground/70 ml-6 shrink-0 leading-none transition-transform duration-400 ease-out group-data-[state=open]:rotate-45">
                        +
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8">
                      <div className="pl-0 md:pl-14 pr-0 md:pr-12 py-2">
                        <p className="font-default text-muted-foreground text-[1.0625rem] leading-[1.7]">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>

              {}
              <div className="mt-16 pt-8 border-t border-border">
                <p className="font-default italic text-lg text-muted-foreground">
                  Need further assistance?{" "}
                  <Link to="/ContactPage" className="text-foreground not-italic font-default relative inline-block group">
                    <span className="inline-block">
                      Contact our team
                      <span className="ml-1 inline-block transition-transform duration-300 group-hover:translate-x-1">
                        &rarr;
                      </span>
                    </span>
                    <span className="absolute left-0 -bottom-0.5 h-px w-full bg-foreground origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-500 ease-out" />
                    <span className="absolute left-0 -bottom-0.5 h-px w-full bg-primary origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out delay-100" />
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}